/**
 * Staff units and offices (1.5.15). Elijah, 5 Oct 2026: "1207, 1208, 1205 are our staff apartments; 1209 is the
 * Foundation office." They take a crew position on the day like any visit, but they are not tenants: no release,
 * no home visit, no agreement, never in a tenant total, never a tenant email.
 *
 * The list is one setting, HK_STAFF_UNITS (Admin → Settings → Housekeeping → "Staff units and offices"): apartment
 * numbers separated by ";", the word "office" after an office — `1205; 1207; 1208; 1209 office`. A row of SM_Tenants
 * in one of these apartments is tagged TenantCategory `Staff` (the import, the re-create and saving the setting do
 * it); everything else in the app asks the category, as since 1.4.0 (`isStaffCategory`).
 */

export const STAFF_UNITS_KEY = 'HK_STAFF_UNITS';
export const DEFAULT_STAFF_UNITS = '1205; 1207; 1208; 1209 office';

export type StaffUnitKind = 'unit' | 'office';
export interface StaffUnit {
  unit: string;
  kind: StaffUnitKind;
  /**
   * 13 Oct 2026 (Eli): a named space with no apartment number — "1st Floor ABF Office", "Event Prep". It is kept as
   * typed, it is an office when its name says office, and the app keeps one staff row for it so Book can reach it.
   */
  named?: boolean;
}

/** How two entries are compared: case, extra spaces and dashes ignored. */
export const unitKey = (unit: string) => unit.trim().toUpperCase().replace(/[\s-]+/g, '');
const norm = (unit: string) => unit.trim().toUpperCase();
const APARTMENT = /^([A-Za-z0-9-]{1,10})(?:\s+\(?(office|oficina)\)?)?$/i;
/** A named space: letters, numbers, spaces and a little punctuation, 2 to 40 characters, starting with a letter or a digit. */
const NAMED = /^[A-Za-z0-9][A-Za-z0-9 &'./#()-]{1,39}$/;

/** The list a setting value holds; null when a part of it is neither an apartment number nor a space's name (the save is refused). */
export function parseStaffUnits(value: string | null | undefined): StaffUnit[] | null {
  const v = String(value ?? '').trim();
  if (!v || v === 'Not set') return [];
  const out: StaffUnit[] = [];
  for (const raw of v.split(/\s*;\s*/).flatMap((p) => (/^[\dA-Za-z-]+(\s*,\s*[\dA-Za-z-]+)+$/.test(p) ? p.split(/\s*,\s*/) : [p])).filter(Boolean)) {
    const part = raw.replace(/\s+/g, ' ').trim();
    const m = part.match(APARTMENT);
    let entry: StaffUnit;
    if (m) entry = { unit: norm(m[1]!), kind: m[2] ? 'office' : 'unit' };
    else if (NAMED.test(part)) entry = { unit: part, kind: /\b(office|oficina)\b/i.test(part) ? 'office' : 'unit', named: true };
    else return null;
    if (!out.some((x) => unitKey(x.unit) === unitKey(entry.unit))) out.push(entry);
  }
  return out;
}

/** The value as it is stored: `1205; 1207; 1209 office; 1st Floor ABF Office; Event Prep`. */
export const formatStaffUnits = (list: StaffUnit[]): string => list.map((x) => (x.named ? x.unit : x.kind === 'office' ? `${x.unit} office` : x.unit)).join('; ');

/**
 * 1.7.8: is this apartment an office on the list a settings read holds (HK_STAFF_UNITS; the built-in list when the
 * row is not there)? An office's visit starts at HK_OFFICE_LENGTH, a staff apartment's at HK_STAFF_LENGTH.
 */
export function isOfficeUnit(settings: Record<string, string | number | null | undefined>, unit: string | null | undefined): boolean {
  const v = settings[STAFF_UNITS_KEY];
  const list = parseStaffUnits(v === undefined || v === null ? DEFAULT_STAFF_UNITS : String(v)) ?? [];
  return !!unit && list.some((x) => unitKey(x.unit) === unitKey(unit) && x.kind === 'office');
}

/** The list's entry for an apartment, if it is on it. */
export const staffUnitOf = (list: StaffUnit[], unit: string | null | undefined): StaffUnit | null => (unit ? (list.find((x) => unitKey(x.unit) === unitKey(unit)) ?? null) : null);
